
/**
 * Module dependencies.
 */

import { useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import forEach from 'lodash/forEach';
import map from 'lodash/map';

/**
 * Export `LanguageLink` type.
 */

export type LanguageLink = {
  href: string,
  isActive: boolean,
  locale: string
};

/**
 * Get hreflang values.
 *
 * The translated url of the current page is only known to the `hreflang` tags
 * rendered by `Metatags`, so they are read straight from the document.
 */

function getHrefValues(): Map<string, string> {
  const linkTags = document.getElementsByTagName('link');
  const hrefValues = new Map();

  forEach(linkTags, (link: HTMLLinkElement) => {
    const hreflang = link.getAttribute('hreflang');
    const href = link.getAttribute('href');

    if (hreflang && href) {
      hrefValues.set(hreflang, href);
    }
  });

  return hrefValues;
}

/**
 * Export `useLanguageLinks` hook.
 */

export function useLanguageLinks(): LanguageLink[] {
  const { events, locale, locales } = useRouter();
  const [hrefValues, setHrefValues] = useState<Map<string, string>>(new Map());

  useEffect(() => {
    const handleRouteChange = () => {
      setHrefValues(getHrefValues());
    };

    handleRouteChange();
    events.on('routeChangeComplete', handleRouteChange);

    return () => {
      events.off('routeChangeComplete', handleRouteChange);
    };
  }, [events]);

  return useMemo(() => map(locales, language => ({
    href: hrefValues.get(language) ?? '/',
    isActive: language === locale,
    locale: language
  })), [hrefValues, locale, locales]);
}
